/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Logic Apps API Service
 *
 * This module has two distinct concerns with different routing strategies:
 *
 * ## Execution APIs (runs, triggers, monitoring)
 * Same REST paths in both modes — the runtime exposes identical endpoints:
 *   Local:  http://localhost:7071/runtime/webhooks/workflow/api/management/...
 *   Cloud:  https://{fqdn}/runtime/webhooks/workflow/api/management/...
 * The only difference is auth: local skips it, cloud attaches token via appFetch.
 * URL construction is centralized in acaAppUrl.ts.
 *
 * ## Authoring APIs (workflow definitions, connections, parameters, CRUD)
 * Completely different backends per mode:
 *   Local:  Vite plugin serves files from disk at /local/workflows/{name}, /local/connections, etc.
 *           Enables hot-reload — edit workflow.json in your IDE, designer updates instantly.
 *   Cloud:  VFS (HTTP file API on the ACA app) at /admin/vfs/... for reads/writes,
 *           plus runtime management APIs for workflow CRUD.
 *
 * The isLocalMode() guards in this file are primarily for the authoring path.
 * See also: draftWorkflowApi.ts (same pattern for draft artifacts).
 *
 * NOTE: This split reflects the current state where StandardRuntimeHost doesn't
 * expose VFS or full workflow CRUD APIs. As FunctionsRuntimeHost or
 * StandardRuntimeHost gain these capabilities, the authoring path could be
 * consolidated to use the same runtime URLs in both modes (like execution already does).
 */

import type {
  Workflow,
  WorkflowDefinitionResponse,
  WorkflowDefinitionFiles,
  ConnectionsResponse,
  Connection,
  ParametersFile,
  Parameter,
  ConnectionsConfiguration,
} from '@/types/logicApps';
import type { ConnectorManifest } from '@/types/connections';
import {
  isLocalMode,
  getLocalRuntimeUrl,
  getLocalPluginUrl,
  localFetch,
} from './localRuntimeClient';
import type { LocalFetchOptions } from './localRuntimeClient';
import { telemetryService } from '@/services/telemetryService';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { AppContext, appFetch, AppFetchError, getAppUrl, getNewVfsUrl, VfsFetchError } from './appClient';
import { WorkflowDefinition } from '@/types';
import { extractResourceName } from '@/types/logicApps';

/**
 * Fetch wrapper that supports both local and cloud modes.
 * In local mode, skips auth.
 * In cloud mode for ACA direct-plane calls, uses token via appFetch.
 * For ARM management calls, uses bearer token.
 */
export const deployArtifacts = async <T>(
  url: string,
  opts: AppContext,
  workflows?: Record<string, WorkflowDefinition>,
  connections?: ConnectionsConfiguration,
  parameters?: Record<string, unknown>,
  notes?: Record<string, unknown>,
): Promise<void> => {
  if (isLocalMode()) {
    await localFetch<T>(url);
    return;
  }

  const accessToken = await opts.getToken();
  if (!accessToken) {
    const errorMessage = 'Failed to get access token for request';
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  const content: any = { files: {} };
  if (workflows) {
    for (const [name, definition] of Object.entries(workflows)) {
      content.files[`${name}/workflow.json`] = definition;
    }
  }

  if (connections) {
    content.files['connections.json'] = connections;
  }

  if (parameters) {
    content.files['parameters.json'] = parameters;
  }

  if (notes) {
    content.files['notes.json'] = notes;
  }

  try {
    await appFetch<T>(url, accessToken, { method: 'POST', body: JSON.stringify(content), cache: 'no-store' });
  } catch (error) {
    if (error instanceof AppFetchError && error.status === 502) {
      return;
    }

    throw error;
  }
  
};

/**
 * Fetch wrapper that supports both local and cloud modes.
 * In local mode, skips auth.
 * In cloud mode for ACA direct-plane calls, uses token via appFetch.
 * For ARM management calls, uses bearer token.
 *
 * `defaultReturn` is forwarded to `appFetch`, which only substitutes it for
 * VFS 404 responses (resource not found) and empty bodies. All other failures
 * — auth, 5xx, network — propagate so callers don't silently fall back to a
 * stale or merged response when the real call actually broke.
 */
const fetchWithAuth = async <T>(url: string, opts: AppContext, defaultReturn?: T, options?: RequestInit): Promise<T> => {
  if (isLocalMode()) {
    return localFetch<T>(url, toLocalFetchOptions(options));
  }

  const accessToken = await opts.getToken();
  if (!accessToken) {
    const errorMessage = 'Failed to get access token for request';
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  return appFetch<T>(url, accessToken, { ...options, cache: 'no-store' }, defaultReturn);
};

const toLocalFetchOptions = (options?: RequestInit): LocalFetchOptions | undefined => {
  if (!options) {
    return undefined;
  }

  const localOptions: LocalFetchOptions = {};

  if (options.method) {
    localOptions.method = options.method as NonNullable<LocalFetchOptions['method']>;
  }

  if (options.body !== undefined && options.body !== null) {
    localOptions.body = normalizeLocalRequestBody(options.body);
  }

  const headers = normalizeLocalHeaders(options.headers);
  if (headers) {
    localOptions.headers = headers;
  }

  if (options.signal !== undefined && options.signal !== null) {
    localOptions.signal = options.signal;
  }

  return localOptions;
};

const normalizeLocalRequestBody = (body: BodyInit): unknown => {
  if (typeof body !== 'string') {
    return body;
  }

  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

const normalizeLocalHeaders = (headers: HeadersInit | undefined): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }

  if (headers instanceof Headers) {
    const normalized: Record<string, string> = {};
    headers.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }

  return headers;
};

/**
 * Runtime workflow format (raw func.exe response)
 */
type RuntimeWorkflow = {
  name: string;
  kind: string;
  isDisabled: boolean;
  triggers: Record<string, { type: string; kind: string }>;
  health?: { state: string };
};

type WorkflowFromApi = {
  id: string;
  name: string;
  kind: string;
  location: string;
  type: string;
  properties: {
    isDisabled: boolean;
    flowState: string;
    health?: { state: string };
  };
};

/**
 * VFS directory entry from Kudu API
 */
type VfsDirectoryEntry = {
  name: string;
  mtime: string; // ISO 8601 timestamp
  crtime?: string; // Creation time (not always available)
  size: number;
  mime: string | null;
  href: string;
  path: string;
};

/**
 * Fetches workflow directory metadata from VFS API.
 * Returns a map of workflow name to mtime (last modified time).
 */
const fetchWorkflowDirectoryMetadata = async (
  opts: AppContext,
): Promise<Map<string, { mtime: string; crtime: string | undefined }>> => {
  const metadataMap = new Map<string, { mtime: string; crtime: string | undefined }>();

  try {
    // In local mode the runtime exposes a Kudu-style VFS API under
    // /admin/vfs/site/wwwroot/ directly (StandardRuntimeHost / func.exe).
    // In cloud mode the Otto control plane proxies VFS under
    // /automationProjects/{p}/applications/{a}/vfs/.
    const url = isLocalMode()
      ? `${getLocalRuntimeUrl()}/admin/vfs/site/wwwroot/?api-version=${apiVersions.otto}`
      : `${getNewVfsUrl(opts)}/?api-version=${apiVersions.otto}`;
    const entries = isLocalMode()
      ? await localFetch<VfsDirectoryEntry[]>(url)
      : await fetchWithAuth<VfsDirectoryEntry[]>(url, opts, []);

    for (const entry of entries) {
      // Only include directories (mime is "inode/directory")
      // and exclude special files/folders like host.json, .funcignore, etc.
      if (entry.mime === 'inode/directory' && !entry.name.startsWith('.') && entry.name !== 'bin') {
        metadataMap.set(entry.name, {
          mtime: entry.mtime,
          crtime: entry.crtime,
        });
      }
    }
  } catch {
    // VFS API might fail due to permissions or other issues
    // Fall back gracefully - workflows will just not have timestamps
    console.warn('Failed to fetch workflow directory metadata from VFS');
  }

  return metadataMap;
};

/**
 * Fetches workflows.
 * In local mode, calls runtime API directly and transforms to ARM format.
 * In cloud mode, calls runtime API and enriches with VFS metadata for timestamps.
 */
export const fetchWorkflows = async (opts: AppContext): Promise<Workflow[]> => {
  if (isLocalMode()) {
    // Runtime exposes workflow list at /runtime/webhooks/workflow/api/management/workflows
    // Returns raw func.exe format - an array of workflows directly.
    // Fetch the list and VFS directory metadata in parallel so we can
    // enrich each workflow with createdTime / changedTime (the runtime
    // management endpoint doesn't expose timestamps).
    const url = `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/workflows?api-version=${apiVersions.otto}`;
    const [response, vfsMetadata] = await Promise.all([
      localFetch<RuntimeWorkflow[]>(url),
      fetchWorkflowDirectoryMetadata(opts),
    ]);

    // Transform runtime format to ARM format expected by portal
    return response.map((w): Workflow => {
      const metadata = vfsMetadata.get(extractResourceName(w.name)) ?? vfsMetadata.get(w.name);
      const changedTime = metadata?.mtime;
      let createdTime: string | undefined;
      if (metadata) {
        if (metadata.crtime && metadata.mtime) {
          createdTime = new Date(metadata.crtime) < new Date(metadata.mtime)
            ? metadata.crtime
            : metadata.mtime;
        } else {
          createdTime = metadata.mtime;
        }
      }

      const workflow: Workflow = {
        id: `/local/workflows/${w.name}`,
        name: w.name,
        type: 'Microsoft.Logic/workflows',
        properties: {
          state: w.isDisabled ? 'Disabled' : 'Enabled',
        },
      };
      if (w.kind) {
        workflow.kind = w.kind as 'Stateful' | 'Stateless' | 'Agent';
      }
      // Only add health if present (exactOptionalPropertyTypes requires this pattern)
      if (w.health) {
        workflow.properties.health = { state: w.health.state as 'Healthy' | 'Unhealthy' | 'Unknown' };
      }
      if (changedTime) {
        workflow.properties.changedTime = changedTime;
      }
      if (createdTime) {
        workflow.properties.createdTime = createdTime;
      }
      return workflow;
    });
  }

  // Fetch workflows from runtime and VFS metadata in parallel
  const [workflowsResult, vfsMetadata] = await Promise.all([
    fetchWithAuth<{ value: WorkflowFromApi[] }>(
      `${getAppUrl(opts)}/workflows?api-version=${apiVersions.otto}`,
      opts,
      { value: []}
    ),
    fetchWorkflowDirectoryMetadata(opts),
  ]);

  const runtimeWorkflows = workflowsResult?.value ?? workflowsResult ?? [];
  // Transform runtime format to Workflow format and enrich with VFS timestamps
  return runtimeWorkflows.map((w): Workflow => {
    // Cloud API often returns workflow names as "appName/workflowName",
    // while VFS directory entries are keyed by the bare workflow name.
    // Normalize the lookup so the join doesn't silently miss every row.
    const metadata = vfsMetadata.get(extractResourceName(w.name)) ?? vfsMetadata.get(w.name);

    // Use mtime for changedTime (last modified)
    const changedTime = metadata?.mtime;

    // For createdTime, use the OLDER of crtime and mtime since Azure's crtime
    // can be reset during deployments/file system recreation
    let createdTime: string | undefined;
    if (metadata) {
      if (metadata.crtime && metadata.mtime) {
        createdTime = new Date(metadata.crtime) < new Date(metadata.mtime)
          ? metadata.crtime
          : metadata.mtime;
      } else {
        createdTime = metadata.mtime;
      }
    }

    const workflow: Workflow = {
      id: `/workflows/${w.name}`,
      name: w.name,
      type: 'Microsoft.Logic/workflows',
      properties: {
        state: w.properties?.isDisabled ? 'Disabled' : 'Enabled',
      },
    };

    if (w.kind) {
      workflow.kind = w.kind as 'Stateful' | 'Stateless' | 'Agent';
    }
    if (w.properties?.health) {
      workflow.properties.health = { state: w.properties.health.state as 'Healthy' | 'Unhealthy' | 'Unknown' };
    }
    if (changedTime) {
      workflow.properties.changedTime = changedTime;
    }
    if (createdTime) {
      workflow.properties.createdTime = createdTime;
    }

    return workflow;
  });
};

type FetchWorkflowArtifactsOptions = AppContext & {
  workflowName: string;
  /** Include draft files in the response (default: true) */
  includeDraft?: boolean;
};

/**
 * Artifact file names following the Azure Logic Apps pattern.
 * Production files: workflow.json, connections.json, parameters.json
 * Draft files: workflow-draft.json, connections-draft.json, parameters-draft.json
 */
export const Artifact = {
  WorkflowFile: 'workflow.json',
  ConnectionsFile: 'connections.json',
  ParametersFile: 'parameters.json',
  NotesFile: 'notes.json',
  DraftWorkflowFile: 'workflow-draft.json',
  DraftConnectionsFile: 'connections-draft.json',
  DraftParametersFile: 'parameters-draft.json',
  DraftNotesFile: 'notes-draft.json',
} as const;

/**
 * Fetches workflow artifacts (definition + connections + optionally draft files).
 */
export const fetchWorkflowArtifacts = async (
  opts: FetchWorkflowArtifactsOptions
): Promise<{files: WorkflowDefinitionFiles| undefined}> => {
  const { includeDraft = true } = opts;
  
  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}`;
    const response = await localFetch<WorkflowDefinitionResponse>(url);
    return { files: response.properties?.files };
  }

  const artifacts = [Artifact.ConnectionsFile, Artifact.ParametersFile];
  const draftArtifacts = [Artifact.DraftConnectionsFile, Artifact.DraftParametersFile];
  const url = `${getAppUrl(opts)}/workflows/${opts.workflowName}?api-version=${apiVersions.otto}&$expand=`;

  // We deliberately read `workflow.json` (and `workflow-draft.json`) directly from VFS
  // rather than relying on whatever the management `?$expand=` endpoint inlines under
  // `properties.files`. The management endpoint can serve a merged/"live" view that
  // mirrors the draft once one exists, which would make the published baseline
  // indistinguishable from the in-progress draft after a draft save + refresh. Hitting
  // VFS guarantees we get the actual on-disk published bytes.
  const promises: Promise<any>[] = [];

  if (includeDraft) {
    promises.push(fetchWithAuth<any>(url + [...artifacts, ...draftArtifacts].join(','), opts, { properties: { files: []}}));
    promises.push(fetchWithAuth<any>(
      `${getNewVfsUrl(opts)}/${opts.workflowName}/${Artifact.DraftWorkflowFile}?api-version=${apiVersions.otto}`,
      opts,
      null, // Return null for 404 so we know draft doesn't exist
    ));
    promises.push(fetchWithAuth<any>(
      `${getNewVfsUrl(opts)}/${opts.workflowName}/${Artifact.DraftNotesFile}?api-version=${apiVersions.otto}`,
      opts,
      null, // Return null for 404 so caller can fall back to management response
    ));
  } else {
    promises.push(fetchWithAuth<any>(url + artifacts.join(','), opts, { properties: { files: []}}));
  }
  promises.push(fetchWithAuth<any>(
    `${getNewVfsUrl(opts)}/${opts.workflowName}/${Artifact.NotesFile}?api-version=${apiVersions.otto}`,
    opts,
    null, // Return null for 404 so caller can fall back to management response
  ));

  const files: WorkflowDefinitionFiles = {};
  const [workflowArtifacts, ...rest] = await Promise.all(promises);
  const draftWorkflow = includeDraft ? rest[0] : null;
  const draftNotes = includeDraft ? rest[1] : null;
  const prodNotes = includeDraft ? rest[2] : rest[0];

  for (const fileName of (Object.keys(workflowArtifacts.properties.files) ?? [])) {
    (files as Record<string, unknown>)[fileName] = workflowArtifacts.properties.files[fileName];
  }

  if (prodNotes) {
    files[Artifact.NotesFile] = prodNotes;
  }

  if (includeDraft && draftWorkflow) {
    files[Artifact.DraftWorkflowFile] = draftWorkflow;
  }

  if (includeDraft && draftNotes) {
    files[Artifact.DraftNotesFile] = draftNotes;
  }

  if (files[Artifact.WorkflowFile] && workflowArtifacts.kind) {
    const wfData = files[Artifact.WorkflowFile] as Record<string, unknown>;
    if (!wfData.kind) {
      files[Artifact.WorkflowFile] = { ...wfData, kind: workflowArtifacts.kind };
    }
  }

  return { files };
};

// =============================================================================
// VFS FILE METADATA (FOR STALE DRAFT DETECTION)
// =============================================================================

/**
 * VFS file metadata entry returned by the Virtual File System API.
 */
export type VfsFileMetadata = {
  name: string;
  /** File size in bytes */
  size: number;
  /** Last modified time (ISO 8601 string) */
  mtime: string;
  /** Creation time (ISO 8601 string) */
  crtime: string;
  /** MIME type */
  mime: string;
  /** Full href to the file */
  href: string;
};

/**
 * Checks if a draft file is stale (production was modified after draft).
 * Returns true if production is newer than draft.
 */
export const isDraftStale = (
  vfsMetadata: VfsFileMetadata[],
  draftFileName: string = 'workflow-draft.json',
  prodFileName: string = 'workflow.json'
): boolean => {
  const draftFile = vfsMetadata.find(f => f.name === draftFileName);
  const prodFile = vfsMetadata.find(f => f.name === prodFileName);

  if (!draftFile || !prodFile) {
    // Can't determine staleness without both files
    return false;
  }

  const draftMtime = new Date(draftFile.mtime).getTime();
  const prodMtime = new Date(prodFile.mtime).getTime();

  // Draft is stale if production was modified AFTER the draft
  return prodMtime > draftMtime;
};

/**
 * Fetches connections configuration.
 * In local mode, calls Otto Backend filesystem API.
 */
export const fetchConnections = async (opts: AppContext): Promise<Connection[]> => {
  if (isLocalMode()) {
    try {
      const url = `${getLocalPluginUrl()}/local/connections`;
      const response = await localFetch<ConnectionsResponse | ConnectionsConfiguration>(url);

      const connections: Connection[] = [];
      const config = unwrapLocalConnectionsResponse(response);

      if (config?.managedApiConnections) {
        for (const [name, conn] of Object.entries(config.managedApiConnections)) {
          connections.push({
            name,
            apiId: conn.api.id,
            connectionId: conn.connection.id,
            type: 'managedApi',
          });
        }
      }

      if (config?.serviceProviderConnections) {
        for (const name of Object.keys(config.serviceProviderConnections)) {
          connections.push({
            name,
            apiId: '',
            connectionId: '',
            type: 'serviceProvider',
          });
        }
      }

      if (config?.agentConnections) {
        for (const [name, conn] of Object.entries(config.agentConnections)) {
          connections.push({
            name,
            apiId: conn.modelProvider?.id ?? '',
            connectionId: '',
            type: 'agent',
          });
        }
      }

      return connections;
    } catch (error) {
      console.warn('Failed to fetch local connections:', error);
      return [];
    }
  }

  const url = `${getNewVfsUrl(opts)}/connections.json?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<ConnectionsConfiguration>(url, opts, {});

    const connections: Connection[] = [];
    const config = response;

    if (config?.managedApiConnections) {
      for (const [name, conn] of Object.entries(config.managedApiConnections)) {
        connections.push({
          name,
          apiId: conn.api.id,
          connectionId: conn.connection.id,
          type: 'managedApi',
        });
      }
    }

    if (config?.serviceProviderConnections) {
      for (const name of Object.keys(config.serviceProviderConnections)) {
        connections.push({
          name,
          apiId: config.serviceProviderConnections[name]?.serviceProvider?.id ?? '',
          connectionId: '',
          type: 'serviceProvider',
        });
      }
    }

    if (config?.agentConnections) {
      for (const [name, conn] of Object.entries(config.agentConnections)) {
        connections.push({
          name,
          apiId: conn.modelProvider?.id ?? '',
          connectionId: '',
          type: 'agent',
        });
      }
    }

    return connections;
  } catch (error) {
    // Connections might not exist yet
    console.warn('Failed to fetch connections:', error);
    return [];
  }
};

/**
 * The local dev plugin (`/local/connections`) returns the ARM-shaped envelope
 * `{ properties: { files: { 'connections.json': <config> } } }` to mirror the
 * management API, whereas the cloud VFS endpoints return the bare
 * `ConnectionsConfiguration`. Callers that expect the config must unwrap the
 * envelope; without this the config is read one level too high, so
 * `agentConnections`/`serviceProviderConnections` appear missing (empty
 * references, "connection not found"). Falls back to the response as-is when it
 * is already unwrapped, so it is safe for both shapes.
 */
export const unwrapLocalConnectionsResponse = (
  response: ConnectionsResponse | ConnectionsConfiguration | null | undefined,
): ConnectionsConfiguration => {
  // An ARM envelope is identified by its `properties` wrapper. When present,
  // the config lives at `properties.files['connections.json']` (absent => empty).
  if (response && typeof response === 'object' && 'properties' in response) {
    return (response as ConnectionsResponse).properties?.files?.['connections.json'] ?? {};
  }
  // Already a bare `ConnectionsConfiguration` (e.g. cloud VFS) — return as-is.
  return (response as ConnectionsConfiguration | null | undefined) ?? {};
};

/**
 * Fetches connections json from the app.
 * In local mode, calls Otto Backend filesystem API.
 */
export const fetchConnectionsJson = async (opts: AppContext): Promise<ConnectionsConfiguration> => {
  if (isLocalMode()) {
    try {
      const url = `${getLocalPluginUrl()}/local/connections`;
      const response = await localFetch<ConnectionsResponse | ConnectionsConfiguration>(url);
      return unwrapLocalConnectionsResponse(response);
    } catch (error) {
      console.warn('Failed to fetch local connections:', error);
      return {};
    }
  }

  const url = `${getNewVfsUrl(opts)}/connections.json?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<ConnectionsConfiguration>(url, opts, {});
    return response;
  } catch (error) {
    // Connections might not exist yet
    console.warn('Failed to fetch connections:', error);
    return {};
  }
};

/**
 * Fetches connections-draft.json from the app via VFS.
 * Falls back to connections.json if the draft file doesn't exist.
 */
export const fetchConnectionsDraftJson = async (opts: AppContext): Promise<ConnectionsConfiguration> => {
  if (isLocalMode()) {
    try {
      // Use the draft endpoint so draft-only connection changes are observed in
      // local mode; the plugin falls back to connections.json when no draft exists.
      const url = `${getLocalPluginUrl()}/local/connections/draft`;
      const response = await localFetch<ConnectionsResponse | ConnectionsConfiguration>(url);
      return unwrapLocalConnectionsResponse(response);
    } catch (error) {
      console.warn('Failed to fetch local draft connections:', error);
      return {};
    }
  }

  const draftUrl = `${getNewVfsUrl(opts)}/connections-draft.json?api-version=${apiVersions.otto}`;

  try {
    // No `defaultReturn` here on purpose: we must let a 404 surface as a
    // `VfsFetchError` so the catch below can fall back to the saved
    // connections.json. Passing `{}` would make `appFetch` swallow the 404 and
    // return an empty config, which would hide every saved connection from any
    // draft-only consumer (e.g. `useMcpConnectionsForApp`).
    const response = await fetchWithAuth<ConnectionsConfiguration>(draftUrl, opts);
    return response;
  } catch (error) {
    // Fall back to connections.json when:
    //   - draft file doesn't exist (404), or
    //   - caller lacks `automationProjects/applications/vfs/read` (403).
    // The 403 case happens for users without draft-edit permissions on the
    // project; treat it as "no draft visible, use the saved file" rather
    // than letting the picker explode.
    const isMissingDraft =
      error instanceof VfsFetchError && (error.status === 404 || error.status === 403);
    if (!isMissingDraft) {
      throw error;
    }
    return fetchConnectionsJson(opts);
  }
};

/**
 * Fetches parameters.
 * In local mode, calls Otto Backend filesystem API.
 */
export const fetchParameters = async (opts: AppContext): Promise<Parameter[]> => {
  if (isLocalMode()) {
    try {
      const url = `${getLocalPluginUrl()}/local/parameters`;
      const response = await localFetch<ParametersFile>(url);

      return Object.entries(response).map(([name, def]) => {
        const param: Parameter = {
          name,
          type: def.type,
          value: def.value,
        };
        if (def.metadata?.description) {
          param.description = def.metadata.description;
        }
        return param;
      });
    } catch (error) {
      console.warn('Failed to fetch local parameters:', error);
      return [];
    }
  }

  const url = `${getNewVfsUrl(opts)}/parameters.json?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<ParametersFile>(url, opts, {});

    return Object.entries(response).map(([name, def]) => {
      const param: Parameter = {
        name,
        type: def.type,
        value: def.value,
      };
      if (def.metadata?.description) {
        param.description = def.metadata.description;
      }
      return param;
    });
  } catch (error) {
    // Parameters file might not exist
    console.warn('Failed to fetch parameters:', error);
    return [];
  }
};

/**
 * Fetches parameters.json as raw JSON from the app.
 * In local mode, calls Otto Backend filesystem API.
 */
export const fetchParametersJson = async (opts: AppContext): Promise<ParametersFile> => {
  if (isLocalMode()) {
    try {
      const url = `${getLocalPluginUrl()}/local/parameters`;
      const response = await localFetch<ParametersFile>(url);
      return response;
    } catch (error) {
      console.warn('Failed to fetch local parameters:', error);
      return {};
    }
  }

  const url = `${getNewVfsUrl(opts)}/parameters.json?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<ParametersFile>(url, opts, {});
    return response;
  } catch (error) {
    // Parameters file might not exist
    console.warn('Failed to fetch parameters:', error);
    return {};
  }
};

/**
 * Fetches parameters-draft.json from the app via VFS.
 * Falls back to parameters.json if the draft file doesn't exist.
 */
export const fetchParametersDraftJson = async (opts: AppContext): Promise<ParametersFile> => {
  if (isLocalMode()) {
    try {
      const url = `${getLocalPluginUrl()}/local/parameters/draft`;
      const response = await localFetch<ParametersFile>(url);
      return response;
    } catch (error) {
      console.warn('Failed to fetch local draft parameters:', error);
      return {};
    }
  }

  const draftUrl = `${getNewVfsUrl(opts)}/parameters-draft.json?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<ParametersFile>(draftUrl, opts, {});
    return response;
  } catch (error) {
    // Only fall back to parameters.json when the draft file doesn't exist (404)
    if (!(error instanceof VfsFetchError && error.status === 404)) {
      throw error;
    }
    return fetchParametersJson(opts);
  }
};

// =============================================================================
// SAVE CONNECTIONS JSON
// =============================================================================

export type SaveConnectionsJsonOptions = AppContext & {
  /** Full connections.json contents to persist */
  connectionsConfig: ConnectionsConfiguration;
};

/**
 * Persists the connections.json file for the app.
 * In local mode, POSTs to the Vite local plugin which writes the file directly.
 * In cloud mode, PUTs the JSON via the VFS admin API.
 */
export const saveConnectionsJson = async (
  opts: SaveConnectionsJsonOptions,
): Promise<{ success: boolean }> => {
  const start = performance.now();

  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/connections`;
    await localFetch(url, {
      method: 'POST',
      body: opts.connectionsConfig,
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveConnectionsJson',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  const url = `${getNewVfsUrl(opts)}/connections.json?api-version=${apiVersions.otto}`;
  await fetchWithAuth(url, opts, /* defaultReturn */undefined, {
    method: 'PUT',
    body: JSON.stringify(opts.connectionsConfig),
  });

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveConnectionsJson',
    method: 'PUT',
    statusCode: '200',
  });
  return { success: true };
};

/**
 * Persists the connections-draft.json file for the app.
 * In local mode, POSTs to the Vite local plugin which writes the file directly.
 * In cloud mode, PUTs the JSON via the VFS admin API.
 */
export const saveDraftConnectionsJson = async (
  opts: SaveConnectionsJsonOptions,
): Promise<{ success: boolean }> => {
  const start = performance.now();

  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/connections/draft`;
    await localFetch(url, {
      method: 'POST',
      body: opts.connectionsConfig,
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveDraftConnectionsJson',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  const url = `${getNewVfsUrl(opts)}/connections-draft.json?api-version=${apiVersions.otto}`;
  await fetchWithAuth(url, opts, /* defaultReturn */undefined, {
    method: 'PUT',
    body: JSON.stringify(opts.connectionsConfig),
  });

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveDraftConnectionsJson',
    method: 'PUT',
    statusCode: '200',
  });
  return { success: true };
};

// =============================================================================
// SAVE PARAMETERS
// =============================================================================

export type SaveParametersOptions = AppContext & {
  /** Full parameters.json contents to persist (raw file shape) */
  parameters: ParametersFile;
};

/**
 * Persists the parameters.json file for the app.
 * In local mode, POSTs to the Vite local plugin which writes the file directly.
 * In cloud mode, PUTs the JSON via the VFS admin API.
 */
export const saveParameters = async (
  opts: SaveParametersOptions,
): Promise<{ success: boolean }> => {
  const start = performance.now();

  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/parameters`;
    await localFetch(url, {
      method: 'POST',
      body: opts.parameters,
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveParameters',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  const url = `${getNewVfsUrl(opts)}/parameters.json?api-version=${apiVersions.otto}`;
  await fetchWithAuth(url, opts, /* defaultReturn */undefined, {
    method: 'PUT',
    body: JSON.stringify(opts.parameters),
  });

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveParameters',
    method: 'PUT',
    statusCode: '200',
  });
  return { success: true };
};

/**
 * Persists the parameters-draft.json file for the app.
 * In local mode, POSTs to the Vite local plugin which writes the file directly.
 * In cloud mode, PUTs the JSON via the VFS admin API.
 */
export const saveDraftParameters = async (
  opts: SaveParametersOptions,
): Promise<{ success: boolean }> => {
  const start = performance.now();

  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/parameters/draft`;
    await localFetch(url, {
      method: 'POST',
      body: opts.parameters,
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveDraftParameters',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  const url = `${getNewVfsUrl(opts)}/parameters-draft.json?api-version=${apiVersions.otto}`;
  await fetchWithAuth(url, opts, /* defaultReturn */undefined, {
    method: 'PUT',
    body: JSON.stringify(opts.parameters),
  });

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveDraftParameters',
    method: 'PUT',
    statusCode: '200',
  });
  return { success: true };
};

type ConnectorManifestResponse = {
  id: string;
  name: string;
  properties: {
    generalInformation: {
      displayName: string;
      description?: string;
      iconUrl: string;
    };
    metadata: {
      brandColor: string;
    };
  };
};

type FetchConnectorManifestOptions = {
  projectName: string;
  connectorName: string;
  getToken: () => Promise<string | null>;
};

export const fetchConnectorManifest = async (
  opts: FetchConnectorManifestOptions,
): Promise<ConnectorManifest> => {
  const url = `${getLogicEndpoint()}/automationProjects/${opts.projectName}/managedApis/${opts.connectorName}?api-version=${apiVersions.otto}`;

  const response = await fetchWithAuth<ConnectorManifestResponse>(url, opts as unknown as AppContext, {
    id: '',
    name: '',
    properties: {
      generalInformation: {
        displayName: '',
        description: undefined,
        iconUrl: ''
      },
      metadata: {
        brandColor: ''
      }
    }
  } as any);

  return {
    id: response.id,
    name: response.name,
    displayName: response.properties.generalInformation.displayName,
    description: response.properties.generalInformation.description,
    iconUri: response.properties.generalInformation.iconUrl,
    brandColor: response.properties.metadata.brandColor,
  };
};

/**
 * Operation Group (Service Provider) response from Logic App runtime API
 * GET /runtime/webhooks/workflow/api/management/OperationGroups
 */
type OperationGroupResponse = {
  name: string;
  id: string;
  type?: string;
  properties: {
    displayName: string;
    description?: string;
    iconUri: string;
    brandColor: number; // Hex number (e.g., 0x0078D4)
    capabilities?: string[];
  };
};

/**
 * Response wrapper from Azure API (contains value array)
 */
type OperationGroupsResponse = {
  value: OperationGroupResponse[];
};

type FetchServiceProviderManifestsOptions = AppContext;


export const convertToCanonicalFormat = (value: string): string => {
  return value?.toLowerCase().replaceAll(/\s/g, '');
}

/**
 * Extracts the Azure region from a location string.
 * ACA FQDNs follow the pattern: {app}.{env}.{region}.azurecontainerapps.io
 * For standard Azure locations (e.g., "East US", "eastus"), normalizes to lowercase without spaces.
 * Falls back to the input (normalized) if pattern doesn't match.
 */
export const extractAzureRegion = (locationOrFqdn: string): string => {
  const acaMatch = locationOrFqdn.match(/\.([a-z0-9]+)\.azurecontainerapps\.io$/i);
  if (acaMatch?.[1]) {
    return acaMatch[1].toLowerCase();
  }
  return locationOrFqdn.toLowerCase().replaceAll(/\s/g, '');
};

/**
 * Converts a hex number to a CSS color string
 */
const hexNumberToColor = (hex: number | string): string => {
  if (typeof hex === 'string') {
    return hex.startsWith('#') ? hex : `#${hex}`;
  }
  // Convert number to 6-digit hex string
  return `#${hex.toString(16).padStart(6, '0')}`;
};

/**
 * Fetches all available service provider manifests from a Logic App.
 * Uses the /OperationGroups API endpoint which returns connector info.
 * Returns a map of serviceProviderId -> ConnectorManifest
 */
export const fetchServiceProviderManifests = async (
  opts: FetchServiceProviderManifestsOptions
): Promise<Map<string, ConnectorManifest>> => {
  // Use OperationGroups API to get all registered service providers
  const url = `${getAppUrl(opts)}/operationGroups?api-version=${apiVersions.otto}`;

  try {
    const response = await fetchWithAuth<OperationGroupsResponse>(url, opts, { value: [] });

    const manifests = new Map<string, ConnectorManifest>();

    for (const provider of response.value) {
      // The id is the serviceProviderId (e.g., "/serviceProviders/AzureBlob")
      manifests.set(provider.id, {
        id: provider.id,
        name: provider.name,
        displayName: provider.properties.displayName,
        description: provider.properties.description,
        iconUri: provider.properties.iconUri,
        brandColor: hexNumberToColor(provider.properties.brandColor),
      });
    }

    return manifests;
  } catch (error) {
    console.warn('Failed to fetch service provider manifests:', error);
    return new Map();
  }
};

/**
 * Service Provider Operation Manifest response
 * GET /runtime/webhooks/workflow/api/management/operationGroups/{group}/operations/{operationId}?$expand=properties/manifest
 */
export type ServiceProviderOperationResponse = {
  name: string;
  id: string;
  type: string;
  properties: {
    api: {
      id: string;
      type: string;
      name: string;
      displayName: string;
      iconUri: string;
      brandColor: string;
      description?: string;
    };
    summary: string;
    description?: string;
    visibility?: string;
    operationType?: string;
    brandColor?: string;
    iconUri?: string;
    manifest?: {
      inputs?: {
        type: string;
        properties?: Record<string, unknown>;
        required?: string[];
      };
      inputsLocation?: string[];
      isInputsOptional?: boolean;
      outputs?: {
        type: string;
        properties?: Record<string, unknown>;
      };
      isOutputsOptional?: boolean;
      settings?: {
        secureData?: Record<string, unknown>;
        trackedProperties?: { scopes?: string[] };
        retryPolicy?: { scopes?: string[] };
        timeout?: { scopes?: string[] };
        concurrency?: { scopes?: string[] };
      };
      connector?: {
        type: string;
        name: string;
        id: string;
        properties?: Record<string, unknown>;
      };
    };
  };
};

type FetchServiceProviderOperationOptions = AppContext & {
  operationGroup: string;
  operationId: string;
};

/**
 * Fetches the manifest for a specific Service Provider operation.
 * This returns the full operation definition including inputs/outputs schema.
 */
export const fetchServiceProviderOperationManifest = async (
  opts: FetchServiceProviderOperationOptions
): Promise<ServiceProviderOperationResponse> => {
  const url = `${getAppUrl(opts)}/operationGroups/${opts.operationGroup}/operations/${opts.operationId}?api-version=${apiVersions.otto}&$expand=properties/manifest`;

  return fetchWithAuth<ServiceProviderOperationResponse>(url, opts);
};

/**
 * Full Service Provider connector response.
 * GET /runtime/webhooks/workflow/api/management/operationGroups/{group}?api-version=...
 *
 * Mirrors Logic Apps Designer's `StandardConnectionService.getConnector()` —
 * the standalone connector endpoint returns the complete `connectionParameterSets`,
 * including auth-provider fields (Authority, Tenant, CredentialType, Pfx, Password
 * for ActiveDirectoryOAuth; Identity for ManagedServiceIdentity) that the
 * `/operations/{op}?$expand=properties/manifest` endpoint drops from its
 * embedded `manifest.connector` copy.
 */
export type ServiceProviderConnectorResponse = {
  type: string;
  name: string;
  id: string;
  properties: {
    displayName?: string;
    description?: string;
    iconUri?: string;
    brandColor?: string;
    capabilities?: string[];
    connectionParameterSets?: { values?: unknown[]; uiDefinition?: unknown };
    isSecureByDefault?: boolean;
    testConnectionOperationName?: string;
    testConnectionUrl?: string;
    [key: string]: unknown;
  };
};

type FetchServiceProviderConnectorOptions = AppContext & {
  operationGroup: string;
};

export const fetchServiceProviderConnector = async (
  opts: FetchServiceProviderConnectorOptions
): Promise<ServiceProviderConnectorResponse> => {
  const url = `${getAppUrl(opts)}/operationGroups/${encodeURIComponent(opts.operationGroup)}?api-version=${apiVersions.otto}`;
  return fetchWithAuth<ServiceProviderConnectorResponse>(url, opts);
};

/**
 * Body for `POST /operationGroups/{group}/testConnection`. Matches LA Designer's
 * `_testServiceProviderConnection` payload: `parameterValues` are raw (no
 * `@appsetting()` refs) with auth-provider fields nested under `authProvider`.
 */
export type ServiceProviderConnectionTestBody = {
  parameterValues: Record<string, unknown>;
  parameterSetName?: string;
  serviceProvider: { id: string };
  displayName: string;
};

type TestServiceProviderConnectionOptions = AppContext & {
  /** Lowercased operation group, e.g. `azureblob` */
  operationGroup: string;
  body: ServiceProviderConnectionTestBody;
};

/**
 * Pre-flight test an SP connection BEFORE persisting it. Only call when the
 * connector exposes `properties.testConnectionUrl` (older SPs without a
 * `TestConnectionOperationName` omit it). Throws on non-2xx so the form can
 * surface auth / cert / RBAC failures inline.
 */
export const testServiceProviderConnection = async (
  opts: TestServiceProviderConnectionOptions
): Promise<void> => {
  const url = `${getAppUrl(opts)}/operationGroups/${encodeURIComponent(opts.operationGroup)}/testConnection?api-version=${apiVersions.otto}`;
  await fetchWithAuth<void>(url, opts, undefined, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts.body),
  });
};

export type WorkflowKind = 'Stateful' | 'Stateless' | 'Agent';

type CreateWorkflowOptions = AppContext & {
  workflowName: string;
  kind?: WorkflowKind | undefined;
};

/**
 * Creates a new workflow by writing workflow.json to VFS.
 * In local mode, calls Otto Backend filesystem API.
 * In cloud mode, writes directly to VFS admin API.
 * @param opts.kind - Workflow kind: 'Stateful' (default), 'Stateless', or 'Agent'
 */
export const createWorkflow = async (opts: CreateWorkflowOptions): Promise<{ name: string }> => {
  const start = performance.now();
  const workflowDefinition = {
    definition: {
      $schema:
        'https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#',
      contentVersion: '1.0.0.0',
      triggers: {},
      actions: {},
    },
    kind: opts.kind ?? 'Stateful',
  };

  if (isLocalMode()) {
    const url = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}`;
    await localFetch(url, {
      method: 'POST',
      body: workflowDefinition,
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'createWorkflow',
      method: 'POST',
      statusCode: '200',
    });
    return { name: opts.workflowName };
  }

  const url = `${getAppUrl(opts)}/deployWorkflowArtifacts?api-version=${apiVersions.otto}`;
  await deployArtifacts(url, opts, { [opts.workflowName]: workflowDefinition });

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'createWorkflow',
    method: 'PUT',
    statusCode: '200',
  });
  return { name: opts.workflowName };
};

// =============================================================================
// SAVE WORKFLOW
// =============================================================================

export type SaveWorkflowOptions = AppContext & {
  workflowName: string;
  /** The complete workflow definition including kind */
  workflowDefinition: {
    definition: Record<string, unknown>;
    kind: string;
  };
  /** Optional connections configuration to save alongside workflow */
  connectionsConfig?: Record<string, unknown> | undefined;
};

/**
 * Saves an existing workflow by writing files to VFS.
 * In local mode, calls Otto Backend filesystem API.
 * In cloud mode, writes workflow.json and optionally connections.json via VFS PUT.
 */
export const saveWorkflow = async (opts: SaveWorkflowOptions): Promise<{ success: boolean }> => {
  const start = performance.now();

  if (isLocalMode()) {
    // Save workflow definition
    const workflowUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}`;
    await localFetch(workflowUrl, {
      method: 'POST',
      body: opts.workflowDefinition,
    });

    // Save connections if provided
    if (opts.connectionsConfig) {
      const connectionsUrl = `${getLocalPluginUrl()}/local/connections`;
      await localFetch(connectionsUrl, {
        method: 'POST',
        body: opts.connectionsConfig,
      });
    }

    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveWorkflow',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  // Write workflow.json via VFS PUT
  const workflowVfsUrl = `${getNewVfsUrl(opts)}/${opts.workflowName}/workflow.json?relativePath=1&api-version=${apiVersions.otto}`;
  await fetchWithAuth(workflowVfsUrl, opts, /*defaultReturn */undefined, {
    method: 'PUT',
    body: JSON.stringify(opts.workflowDefinition),
  });

  // Write connections.json if provided
  if (opts.connectionsConfig) {
    const connectionsVfsUrl = `${getNewVfsUrl(opts)}/${opts.workflowName}/connections.json?relativePath=1&api-version=${apiVersions.otto}`;
    await fetchWithAuth(connectionsVfsUrl, opts, /*defaultReturn */undefined, {
      method: 'PUT',
      body: JSON.stringify(opts.connectionsConfig),
    });
  }

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveWorkflow',
    method: 'PUT',
    statusCode: '200',
  });
  return { success: true };
};

// =============================================================================
// DELETE WORKFLOW
// =============================================================================

type DeleteWorkflowOptions = AppContext & {
  workflowNames: string[];
};

/**
 * Delete one or more workflows.
 * In cloud mode this uses deployWorkflowArtifacts with filesToDelete so the runtime
 * removes both the workflow definition file and the workflow directory.
 */
export const deleteWorkflows = async (opts: DeleteWorkflowOptions): Promise<void> => {
  if (isLocalMode()) {
    for (const name of opts.workflowNames) {
      const url = `${getLocalPluginUrl()}/local/workflows/${name}`;
      await localFetch(url, { method: 'DELETE' });
    }
    return;
  }

  if (opts.workflowNames.length === 0) {
    return;
  }

  const accessToken = await opts.getToken();
  if (!accessToken) {
    throw new Error('Failed to get access token for request');
  }

  const url = `${getAppUrl(opts)}/deployWorkflowArtifacts?api-version=${apiVersions.otto}`;
  const filesToDelete = opts.workflowNames.flatMap((name) => [`${name}/workflow.json`, `${name}/workflow-draft.json`, `${name}/`]);

  await appFetch(url, accessToken, {
    method: 'POST',
    body: JSON.stringify({ filesToDelete }),
  });
};
